import ChessNav from './ChessNav';
import React, { useState, useEffect } from 'react';
import Icon from '../Shared/Icons';
import { useNavigate } from 'react-router-dom';
import { db } from '../../config/firebase';
import { collection, query, where, getDocs, doc, onSnapshot, setDoc, serverTimestamp, limit } from 'firebase/firestore';
import { XAxis, YAxis, Tooltip, ResponsiveContainer, AreaChart, Area } from 'recharts';
import { DEFAULT_RATING, getTier } from '../../utils/chessRating';
import { getBoardTheme, getPieceTheme, nextBoardTheme, nextPieceTheme } from '../../utils/chessThemes';
import { C } from '../../utils/chessTheme';

const PLAYER_KEY = 'iyanna_chess_player';

function loadPlayer() {
  try {
    const raw = localStorage.getItem(PLAYER_KEY);
    return raw ? JSON.parse(raw) : null;
  } catch { return null; }
}

const BADGES = [
  { id: 'first_win', label: 'Ushindi wa Kwanza', icon: 'trophy', check: (s) => s.wins >= 1 },
  { id: 'five_wins', label: '5 Ushindi', icon: 'flag', check: (s) => s.wins >= 5 },
  { id: 'ten_games', label: 'Mchezaji Hodari', icon: 'bolt', check: (s) => s.gamesPlayed >= 10 },
  { id: 'rating_1400', label: 'Twiga Tier', icon: 'seedling', check: (s) => s.rating >= 1400 },
  { id: 'rating_1800', label: 'Chui Tier', icon: 'sparkle', check: (s) => s.rating >= 1800 },
  { id: 'no_loss_streak', label: 'Hajawahi Kushindwa', icon: 'sparkle', check: (s) => s.gamesPlayed >= 3 && s.losses === 0 },
];

export default function ChessProfile() {
  const navigate = useNavigate();
  const [player] = useState(loadPlayer());
  const [games, setGames] = useState([]);
  const [loading, setLoading] = useState(true);
  const [boardTheme, setBoardTheme] = useState(getBoardTheme());
  const [meDoc, setMeDoc] = useState(null);           // live: coins, verified
  const [verif, setVerif] = useState(null);           // my verification request
  const [showVerifForm, setShowVerifForm] = useState(false);
  const [vForm, setVForm] = useState({ fullName: '', age: '', school: '', guardianPhone: '' });
  const [vSaving, setVSaving] = useState(false);
  const [pieceTheme, setPieceTheme] = useState(getPieceTheme());

  useEffect(() => {
    if (!player) { navigate('/chess'); return; }
    (async () => {
      try {
        // Single-field queries (no composite index needed) — filter to
        // finished games on the client. This is what makes history work
        // reliably without any Firestore index setup.
        const [whiteSnap, blackSnap] = await Promise.all([
          getDocs(query(collection(db, 'chessGames'), where('white.id', '==', player.id))),
          getDocs(query(collection(db, 'chessGames'), where('black.id', '==', player.id))),
        ]);
        const all = [
          ...whiteSnap.docs.map(d => ({ id: d.id, color: 'white', ...d.data() })),
          ...blackSnap.docs.map(d => ({ id: d.id, color: 'black', ...d.data() })),
        ]
          .filter(g => g.status === 'finished')
          .sort((a, b) => (a.createdAt?.seconds || 0) - (b.createdAt?.seconds || 0));
        setGames(all);
      } catch (e) {
        console.error(e);
      }
      setLoading(false);
    })();
  }, [player, navigate]);

  // Live copy of my public leaderboard entry — the SAME record the
  // ranking shows. Queried by nameLower so it's authoritative even if the
  // cached id drifts; carries rating, coins and verified. Repairs the
  // localStorage rating so Home and seats match after any admin change.
  useEffect(() => {
    if (!player?.name) return;
    const nameLower = player.name.toLowerCase();
    const qy = query(collection(db, 'chessPlayers'), where('nameLower', '==', nameLower), limit(1));
    const unsub = onSnapshot(qy, snap => {
      if (snap.empty) return;
      const data = snap.docs[0].data();
      setMeDoc(data);
      if (typeof data.rating === 'number' && data.rating !== player.rating) {
        try {
          const raw = localStorage.getItem(PLAYER_KEY);
          if (raw) {
            const obj = JSON.parse(raw);
            obj.rating = data.rating;
            localStorage.setItem(PLAYER_KEY, JSON.stringify(obj));
          }
        } catch {}
      }
    });
    return unsub;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [player?.name]);

  // My verification request status (pending / approved / rejected)
  useEffect(() => {
    if (!player?.id) return;
    const unsub = onSnapshot(doc(db, 'chessVerifications', player.id), snap => {
      setVerif(snap.exists() ? snap.data() : null);
    });
    return unsub;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [player?.id]);

  if (!player) return null;

  const wins = games.filter(g => g.winner === g.color).length;
  const losses = games.filter(g => g.winner && g.winner !== 'draw' && g.winner !== g.color).length;
  const draws = games.filter(g => g.winner === 'draw').length;
  const gamesPlayed = games.length;
  const liveRating = (typeof meDoc?.rating === 'number') ? meDoc.rating : player.rating;
  const tier = getTier(liveRating);

  const stats = { rating: liveRating, wins, losses, draws, gamesPlayed };
  const savedGames = games.filter(g => g.isSaved);
  const earnedBadges = BADGES.filter(b => b.check(stats));

  async function submitVerification(e) {
    e.preventDefault();
    if (!vForm.fullName.trim()) return;
    setVSaving(true);
    try {
      await setDoc(doc(db, 'chessVerifications', player.id), {
        playerId: player.id,
        playerName: player.name,
        fullName: vForm.fullName.trim(),
        age: vForm.age.trim(),
        school: vForm.school.trim(),
        guardianPhone: vForm.guardianPhone.trim(),
        status: 'pending',
        submittedAt: serverTimestamp(),
      });
      setShowVerifForm(false);
    } catch (err) { console.error(err); }
    setVSaving(false);
  }

  // Approximate rating history: each game recorded the player's rating
  // AT THE TIME it was created, plus the current live rating as the final point.
  const ratingHistory = games.map((g, i) => ({
    name: `#${i + 1}`,
    rating: g.color === 'white' ? (g.white?.rating || DEFAULT_RATING) : (g.black?.rating || DEFAULT_RATING),
  }));
  ratingHistory.push({ name: 'Now', rating: liveRating });

  return (
    <div style={styles.page}>
      <div style={styles.topBar}>
        <button style={styles.backBtn} onClick={() => navigate('/chess')}>
          <Icon name="door" size={22} color={C.text} strokeWidth={2.6} />
        </button>
        <div style={styles.topTitle}>Profile</div>
        <div style={{ width: '44px' }} />
      </div>

      {/* PROFILE CARD */}
      <div style={styles.profileCard}>
        <div style={styles.avatarCircle}>{player.name?.[0]?.toUpperCase() || '♟'}</div>
        <div style={styles.profileName}>
          {player.name}
          {meDoc?.verified && <Icon name="verified" size={16} color="#34D399" strokeWidth={2.2} style={{ marginLeft: 6 }} />}
        </div>
        <div style={{ ...styles.profileTier, color: tier.color }}>{tier.swahili}</div>
        <div style={styles.ratingBig}>{liveRating}</div>
        <div style={{ ...styles.ratingLabel, marginTop: '2px' }}>Rating ya Mazoezi</div>
        <div
          onClick={() => navigate('/rating')}
          style={{ fontSize: '11px', color: '#9CA3AF', marginTop: '4px', cursor: 'pointer', textDecoration: 'underline' }}
        >
          Tazama Rating Rasmi (IFR) →
        </div>
      </div>

      {/* STATS ROW */}
      <div style={styles.statsRow}>
        <div style={styles.statBox}><div style={styles.statNum}>{wins}</div><div style={styles.statLbl}>Ushindi</div></div>
        <div style={styles.statBox}><div style={styles.statNum}>{losses}</div><div style={styles.statLbl}>Kushindwa</div></div>
        <div style={styles.statBox}><div style={styles.statNum}>{draws}</div><div style={styles.statLbl}>Sare</div></div>
        <div style={styles.statBox}><div style={styles.statNum}>{gamesPlayed}</div><div style={styles.statLbl}>Michezo</div></div>
      </div>

      {/* COINS */}
      <div style={styles.coinCard}>
        <div style={styles.coinLeft}>
          <div style={styles.coinIconWrap}><Icon name="coin" size={22} color="#FBBF24" strokeWidth={2} /></div>
          <div>
            <div style={styles.coinNum}>{meDoc?.coins ?? 0}</div>
            <div style={styles.coinLbl}>Coins zako</div>
          </div>
        </div>
        <div style={styles.coinNote}>
          Coins zinapatikana kwenye tournaments na zawadi za watazamaji —
          zinabadilishwa kuwa zawadi halisi na Iyanna Foundation.
        </div>
      </div>

      {/* VERIFICATION — jisajili uthibitishwe na Iyanna */}
      <div style={styles.verifCard}>
        {meDoc?.verified ? (
          <div style={styles.verifDone}>
            <Icon name="verified" size={20} color="#34D399" strokeWidth={2} />
            <div>
              <div style={styles.verifTitle}>Umethibitishwa na Iyanna</div>
              <div style={styles.verifText}>Wewe ni mchezaji rasmi wa Iyanna Chess. Hongera!</div>
            </div>
          </div>
        ) : verif?.status === 'pending' ? (
          <div style={styles.verifDone}>
            <Icon name="clock" size={20} color="#FBBF24" strokeWidth={2} />
            <div>
              <div style={styles.verifTitle}>Ombi lako linasubiri</div>
              <div style={styles.verifText}>Timu ya Iyanna inapitia usajili wako — utathibitishwa hivi karibuni.</div>
            </div>
          </div>
        ) : !showVerifForm ? (
          <>
            <div style={styles.verifTitle}>Jisajili — Uthibitishwe na Iyanna</div>
            <div style={styles.verifText}>
              Jiandikishe rasmi ili upate beji ya uthibitisho na kuhesabika kwenye
              familia ya wachezaji wa Iyanna Foundation.
            </div>
            <button style={styles.verifBtn} onClick={() => setShowVerifForm(true)}>
              <Icon name="verified" size={15} color="#06281A" strokeWidth={2.2} /> Anza Usajili
            </button>
          </>
        ) : (
          <form onSubmit={submitVerification} style={styles.verifForm}>
            <input style={styles.verifInput} placeholder="Jina kamili" value={vForm.fullName}
              onChange={e => setVForm(f => ({ ...f, fullName: e.target.value }))} required />
            <input style={styles.verifInput} placeholder="Umri" value={vForm.age}
              onChange={e => setVForm(f => ({ ...f, age: e.target.value }))} />
            <input style={styles.verifInput} placeholder="Shule / Darasa" value={vForm.school}
              onChange={e => setVForm(f => ({ ...f, school: e.target.value }))} />
            <input style={styles.verifInput} placeholder="Simu ya mzazi/mlezi" value={vForm.guardianPhone}
              onChange={e => setVForm(f => ({ ...f, guardianPhone: e.target.value }))} />
            <div style={styles.verifFormBtns}>
              <button type="submit" style={styles.verifBtn} disabled={vSaving}>
                {vSaving ? 'Inatuma…' : 'Tuma Usajili'}
              </button>
              <button type="button" style={styles.verifCancel} onClick={() => setShowVerifForm(false)}>Ghairi</button>
            </div>
          </form>
        )}
      </div>

      {/* RATING HISTORY CHART */}
      {ratingHistory.length > 1 && (
        <div style={styles.chartCard}>
          <div style={styles.sectionLabel}>Safari ya Rating</div>
          <div style={{ width: '100%', height: 190 }}>
            <ResponsiveContainer>
              <AreaChart data={ratingHistory} margin={{ top: 6, right: 6, left: 0, bottom: 0 }}>
                <defs>
                  <linearGradient id="ratingFill" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor="#34D399" stopOpacity={0.35} />
                    <stop offset="100%" stopColor="#34D399" stopOpacity={0} />
                  </linearGradient>
                </defs>
                <XAxis dataKey="name" tick={{ fill: 'rgba(255,255,255,0.35)', fontSize: 10 }} axisLine={false} tickLine={false} />
                <YAxis domain={['dataMin - 30', 'dataMax + 30']} tick={{ fill: 'rgba(255,255,255,0.35)', fontSize: 10 }} width={34} axisLine={false} tickLine={false} />
                <Tooltip contentStyle={{ background: '#1B201E', border: '1px solid rgba(52,211,153,0.3)', borderRadius: 10 }} labelStyle={{ color: '#9CA3AF' }} itemStyle={{ color: '#34D399' }} />
                <Area type="monotone" dataKey="rating" stroke="#34D399" strokeWidth={2.5} fill="url(#ratingFill)" dot={false} activeDot={{ r: 4, fill: '#6EE7A8' }} />
              </AreaChart>
            </ResponsiveContainer>
          </div>
        </div>
      )}

      {/* BADGES */}
      <div style={styles.sectionLabel}>Achievements</div>
      <div style={styles.badgeGrid}>
        {BADGES.map(b => {
          const earned = earnedBadges.some(e => e.id === b.id);
          return (
            <div key={b.id} style={{ ...styles.badgeCard, opacity: earned ? 1 : 0.3 }}>
              <div style={styles.badgeIcon}><Icon name={b.icon} size={22} color="#4ADE80" /></div>
              <div style={styles.badgeLabel}>{b.label}</div>
            </div>
          );
        })}
      </div>

      {/* MY SAVED VIDEOS */}
      {savedGames.length > 0 && (
        <>
          <div style={styles.savedHead}>
            <div style={styles.sectionLabel}>Video Zangu Zilizohifadhiwa</div>
            <button style={styles.savedAllBtn} onClick={() => navigate('/chess/watch')}>
              <Icon name="play" size={13} color="#06281A" strokeWidth={2.5} /> Angalia Zote
            </button>
          </div>
          <div style={styles.savedRow}>
            {savedGames.map(g => {
              const opp = g.color === 'white' ? g.black?.name : g.white?.name;
              const won = g.winner === g.color;
              return (
                <button key={g.id} style={styles.savedCard} onClick={() => navigate(`/chess/review/${g.id}`)}>
                  <div style={styles.savedThumb}><Icon name="play" size={18} color="#34D399" /></div>
                  <div style={styles.savedVs}>vs {opp || 'Mpinzani'}</div>
                  <div style={{ ...styles.savedResult, color: won ? '#5FD08A' : '#FF9A90' }}>{won ? 'Ushindi' : g.winner === 'draw' ? 'Sare' : 'Kushindwa'}</div>
                </button>
              );
            })}
          </div>
        </>
      )}

      {/* GAME HISTORY */}
      <div style={styles.sectionLabel}>Game History</div>
      {loading ? (
        <div style={styles.empty}>Inapakia...</div>
      ) : games.length === 0 ? (
        <div style={styles.empty}>Hujacheza mchezo bado. Rudi Lobby ucheze wa kwanza!</div>
      ) : (
        <div style={styles.gameList}>
          {[...games].reverse().map(g => {
            const won = g.winner === g.color;
            const drew = g.winner === 'draw';
            const opponent = g.color === 'white' ? g.black?.name : g.white?.name;
            return (
              <div key={g.id} style={styles.gameRow} onClick={() => navigate(`/chess/review/${g.id}`)}>
                <span style={{
                  ...styles.resultTag,
                  background: drew ? 'rgba(255,255,255,0.1)' : won ? 'rgba(95,208,138,0.16)' : 'rgba(229,100,91,0.16)',
                  color: drew ? '#C9C9C9' : won ? '#5FD08A' : '#FF9A90',
                }}>
                  {drew ? 'Sare' : won ? 'Ushindi' : 'Kushindwa'}
                </span>
                <span style={styles.opponentName}>vs {opponent || 'Opponent'}</span>
                <span style={styles.reviewTag}>Review →</span>
              </div>
            );
          })}
        </div>
      )}

      {/* HUB MENU — everything else lives here, one tap away */}
      <div style={styles.sectionLabel}>Zaidi</div>
      <div style={styles.menuList}>
        {[
          { icon: 'book', label: 'Jifunze Chess', path: '/chess/learn' },
          { icon: 'info', label: 'Jinsi ya Kucheza', path: '/chess/guide' },
          { icon: 'trophy', label: 'Mashindano', path: '/chess/tournaments' },
          { icon: 'crown', label: 'Hall of Fame', path: '/chess/hall-of-fame' },
          { icon: 'heart', label: 'Iyanna Foundation', path: '/' },
        ].map((m, i) => (
          <button key={i} style={styles.menuRow} onClick={() => navigate(m.path)}>
            <div style={styles.menuIcon}><Icon name={m.icon} size={18} color="#34D399" /></div>
            <div style={{ flex: 1, minWidth: 0, textAlign: 'left' }}>
              <div style={styles.menuLabel}>{m.label}</div>
              {m.sub && <div style={styles.menuSub}>{m.sub}</div>}
            </div>
            <span style={styles.chevron}>›</span>
          </button>
        ))}
      </div>

      {/* THEME SETTINGS */}
      <div style={styles.sectionLabel}>Mandhari</div>
      <div style={styles.themePanel}>
        <button style={styles.themeRow} onClick={() => setBoardTheme(nextBoardTheme())}>
          <div style={{ flex: 1, textAlign: 'left' }}>
            <div style={styles.menuLabel}>Rangi ya Ubao</div>
            <div style={styles.menuSub}>{boardTheme.name} · bofya kubadilisha</div>
          </div>
          <div style={styles.themePreview}>
            <div style={{ ...styles.themeSquare, background: boardTheme.light }} />
            <div style={{ ...styles.themeSquare, background: boardTheme.dark }} />
          </div>
        </button>
        <button style={{ ...styles.themeRow, borderBottom: 'none' }} onClick={() => setPieceTheme(nextPieceTheme())}>
          <div style={{ flex: 1, textAlign: 'left' }}>
            <div style={styles.menuLabel}>Rangi ya Vipande</div>
            <div style={styles.menuSub}>{pieceTheme.name} · bofya kubadilisha</div>
          </div>
          <div style={styles.themePreview}>
            <span style={{ ...styles.themeGlyph, color: pieceTheme.w, WebkitTextStroke: `1px ${pieceTheme.wStroke}` }}>♚{'\uFE0E'}</span>
            <span style={{ ...styles.themeGlyph, color: pieceTheme.b, WebkitTextStroke: `1px ${pieceTheme.bStroke}` }}>♚{'\uFE0E'}</span>
          </div>
        </button>
      </div>

      <ChessNav />
    </div>
  );
}

const styles = {
  savedHead: { maxWidth: '760px', margin: '0 auto', display: 'flex', alignItems: 'center', justifyContent: 'space-between' },
  savedAllBtn: { display: 'inline-flex', alignItems: 'center', gap: '5px', background: 'linear-gradient(135deg, #6EE7A8, #34D399)', color: '#06281A', border: 'none', padding: '7px 13px', borderRadius: '99px', fontSize: '11.5px', fontWeight: '800', cursor: 'pointer', fontFamily: 'inherit' },
  savedRow: { maxWidth: '760px', margin: '0 auto 8px', display: 'flex', gap: '10px', overflowX: 'auto', padding: '4px 0 8px' },
  savedCard: { flexShrink: 0, width: '128px', background: 'linear-gradient(160deg, #262D2A, #1E2421)', border: '1px solid rgba(255,255,255,0.08)', borderRadius: '14px', padding: '12px', cursor: 'pointer', fontFamily: 'inherit', textAlign: 'left' },
  savedThumb: { width: '100%', height: '58px', borderRadius: '10px', background: 'rgba(52,211,153,0.1)', border: '1px solid rgba(52,211,153,0.2)', display: 'flex', alignItems: 'center', justifyContent: 'center', marginBottom: '9px' },
  savedVs: { color: '#ECEFEA', fontSize: '12px', fontWeight: '800', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' },
  savedResult: { fontSize: '10.5px', fontWeight: '800', marginTop: '3px' },
  coinCard: { maxWidth: '760px', margin: '0 auto 14px', display: 'flex', alignItems: 'center', gap: '14px', flexWrap: 'wrap', background: 'linear-gradient(150deg, rgba(251,191,36,0.1), rgba(251,191,36,0.03))', border: '1px solid rgba(251,191,36,0.3)', borderRadius: '16px', padding: '14px 16px' },
  coinLeft: { display: 'flex', alignItems: 'center', gap: '12px' },
  coinIconWrap: { width: '44px', height: '44px', borderRadius: '13px', background: 'rgba(251,191,36,0.12)', display: 'flex', alignItems: 'center', justifyContent: 'center' },
  coinNum: { color: '#FBBF24', fontFamily: "'Fraunces', Georgia, serif", fontSize: '22px', fontWeight: '800', lineHeight: 1 },
  coinLbl: { color: 'rgba(236,239,234,0.55)', fontSize: '10.5px', fontWeight: '800', letterSpacing: '0.5px', textTransform: 'uppercase', marginTop: '3px' },
  coinNote: { flex: 1, minWidth: '200px', color: 'rgba(236,239,234,0.55)', fontSize: '11.5px', lineHeight: 1.6 },
  verifCard: { maxWidth: '760px', margin: '0 auto 14px', background: 'rgba(255,255,255,0.045)', border: '1px solid rgba(52,211,153,0.3)', borderRadius: '16px', padding: '16px' },
  verifDone: { display: 'flex', alignItems: 'flex-start', gap: '12px' },
  verifTitle: { color: '#ECEFEA', fontSize: '14px', fontWeight: '800' },
  verifText: { color: 'rgba(236,239,234,0.6)', fontSize: '12px', lineHeight: 1.6, marginTop: '4px', marginBottom: '10px' },
  verifBtn: { display: 'inline-flex', alignItems: 'center', gap: '7px', background: 'linear-gradient(135deg, #0E9F6E, #34D399)', color: '#06281A', border: 'none', padding: '11px 18px', borderRadius: '11px', fontSize: '12.5px', fontWeight: '800', cursor: 'pointer', fontFamily: 'inherit' },
  verifCancel: { background: 'transparent', border: '1px solid rgba(255,255,255,0.2)', color: 'rgba(236,239,234,0.7)', padding: '11px 16px', borderRadius: '11px', fontSize: '12.5px', fontWeight: '700', cursor: 'pointer', fontFamily: 'inherit' },
  verifForm: { display: 'flex', flexDirection: 'column', gap: '8px' },
  verifFormBtns: { display: 'flex', gap: '8px', marginTop: '4px' },
  verifInput: { background: 'rgba(255,255,255,0.06)', border: '1px solid rgba(255,255,255,0.14)', borderRadius: '10px', padding: '11px 13px', color: '#ECEFEA', fontSize: '13px', fontFamily: 'inherit', outline: 'none' },

  page: { minHeight: '100vh', background: 'radial-gradient(circle at 30% 0%, #262D2A 0%, #1B201E 48%, #121614 100%)', padding: '20px 16px 60px', paddingBottom: '100px' },
  header: { maxWidth: '520px', margin: '0 auto 20px' },
  brandRow: { display: 'flex', alignItems: 'center', gap: '12px' },
  brandLogo: { width: '42px', height: '42px', borderRadius: '50%', background: '#121614', padding: '0px', border: '2px solid rgba(255,255,255,0.15)', overflow: 'hidden'},
  topBar: { maxWidth: '760px', margin: '0 auto 16px', display: 'flex', alignItems: 'center', justifyContent: 'space-between' },
  backBtn: { width: '44px', height: '44px', borderRadius: '13px', background: 'rgba(255,255,255,0.07)', border: '1px solid rgba(255,255,255,0.1)', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' },
  topTitle: { color: '#ECEFEA', fontFamily: "'Fraunces', Georgia, serif", fontSize: '20px', fontWeight: '800' },
  brandName: { fontFamily: "'Fraunces', Georgia, serif", fontSize: '18px', color: '#fff', fontWeight: '700' },
  brandSub: { fontSize: '11px', color: 'rgba(255,255,255,0.5)', textDecoration: 'none' },
  profileCard: { maxWidth: '520px', margin: '0 auto 16px', background: 'rgba(255,255,255,0.07)', border: '1px solid rgba(255,255,255,0.12)', borderRadius: '20px', padding: '26px', textAlign: 'center' },
  avatarCircle: { width: '64px', height: '64px', borderRadius: '50%', background: 'linear-gradient(135deg, #10B981, #34D399)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '28px', fontWeight: '700', color: '#fff', margin: '0 auto 12px' },
  profileName: { color: '#fff', fontFamily: "'Fraunces', Georgia, serif", fontSize: '19px', marginBottom: '3px' },
  profileTier: { fontSize: '12.5px', fontWeight: '700', marginBottom: '10px' },
  ratingBig: { fontFamily: "'Fraunces', Georgia, serif", fontSize: '36px', color: '#34D399', fontWeight: '700', lineHeight: 1 },
  ratingLabel: { fontSize: '11px', color: 'rgba(255,255,255,0.4)', marginTop: '2px' },
  statsRow: { maxWidth: '520px', margin: '0 auto 16px', display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '10px' },
  statBox: { background: 'rgba(255,255,255,0.06)', border: '1px solid rgba(255,255,255,0.1)', borderRadius: '12px', padding: '12px 8px', textAlign: 'center' },
  statNum: { fontFamily: "'Fraunces', Georgia, serif", fontSize: '20px', color: '#fff', fontWeight: '700' },
  statLbl: { fontSize: '10.5px', color: 'rgba(255,255,255,0.5)', marginTop: '2px' },
  chartCard: { maxWidth: '520px', margin: '0 auto 20px', background: 'rgba(255,255,255,0.06)', border: '1px solid rgba(255,255,255,0.1)', borderRadius: '14px', padding: '16px' },
  sectionLabel: { color: 'rgba(255,255,255,0.65)', fontSize: '12px', fontWeight: '700', letterSpacing: '0.5px', textTransform: 'uppercase', maxWidth: '520px', margin: '0 auto 10px' },
  badgeGrid: { maxWidth: '520px', margin: '0 auto 20px', display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '10px' },
  badgeCard: { background: 'rgba(255,255,255,0.06)', border: '1px solid rgba(255,255,255,0.1)', borderRadius: '12px', padding: '14px 8px', textAlign: 'center' },
  badgeIcon: { fontSize: '24px', marginBottom: '6px' },
  badgeLabel: { fontSize: '10px', color: '#fff', lineHeight: 1.3 },
  empty: { maxWidth: '520px', margin: '0 auto 20px', color: 'rgba(255,255,255,0.4)', fontSize: '13px' },
  gameList: { maxWidth: '520px', margin: '0 auto 20px', display: 'flex', flexDirection: 'column', gap: '8px' },
  gameRow: { display: 'flex', alignItems: 'center', gap: '10px', background: 'rgba(255,255,255,0.06)', border: '1px solid rgba(255,255,255,0.1)', borderRadius: '12px', padding: '12px 16px', cursor: 'pointer' },
  resultTag: { fontSize: '10.5px', fontWeight: '800', padding: '4px 10px', borderRadius: '20px', flexShrink: 0 },
  opponentName: { color: '#fff', fontSize: '13px', flex: 1 },
  reviewTag: { color: '#34D399', fontSize: '12px', fontWeight: '600' },
  menuList: { maxWidth: '520px', margin: '0 auto 20px', display: 'flex', flexDirection: 'column', gap: '8px' },
  menuRow: { display: 'flex', alignItems: 'center', gap: '12px', width: '100%', background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.1)', borderRadius: '14px', padding: '13px 15px', cursor: 'pointer', fontFamily: 'inherit' },
  menuIcon: { width: '36px', height: '36px', borderRadius: '11px', background: 'rgba(16,185,129,0.12)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 },
  menuLabel: { color: '#fff', fontSize: '13.5px', fontWeight: '700' },
  menuSub: { color: 'rgba(255,255,255,0.45)', fontSize: '11px', marginTop: '1px' },
  chevron: { color: 'rgba(255,255,255,0.3)', fontSize: '19px', flexShrink: 0 },
  themePanel: { maxWidth: '520px', margin: '0 auto 20px', background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.1)', borderRadius: '14px', padding: '4px 8px' },
  themeRow: { width: '100%', display: 'flex', alignItems: 'center', gap: '12px', background: 'transparent', border: 'none', borderBottom: '1px solid rgba(255,255,255,0.07)', padding: '13px 6px', cursor: 'pointer', fontFamily: 'inherit' },
  themePreview: { display: 'flex', alignItems: 'center', gap: '3px', flexShrink: 0 },
  themeSquare: { width: '22px', height: '22px', borderRadius: '5px', border: '1px solid rgba(255,255,255,0.15)' },
  themeGlyph: { fontSize: '22px', lineHeight: 1, fontFamily: "'Segoe UI Symbol', 'Noto Sans Symbols 2', 'DejaVu Sans', sans-serif" },
  learnPromo: { maxWidth: '520px', margin: '10px auto 0', background: 'rgba(255,255,255,0.06)', border: '1px dashed rgba(255,255,255,0.25)', borderRadius: '12px', padding: '14px', textAlign: 'center', color: 'rgba(255,255,255,0.7)', fontSize: '12.5px', cursor: 'pointer' },
};
